import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Td,
  Text,
  Textarea,
  Tooltip,
  Tr,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { AddIcon, EditIcon, WarningIcon } from "@chakra-ui/icons";
import { FaCalendarAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  createInterviewForMatch,
  getMatch,
  listInterviewsForMatch,
  transitionMatchWorkflow,
  updateInterview,
  updateMatch
} from "../../../api/matchesApi";
import { getConsultant } from "../../../api/consultantsApi";
import {
  NEED_STATUS_COLORS,
  NEED_STATUS_LABELS,
  NEED_STATUS_LABEL_KEYS,
  CONSULTANT_STATUS_LABELS,
  CONSULTANT_STATUS_LABEL_KEYS,
  MATCH_ASSIGNMENT_STATUSES,
  MATCH_ASSIGNMENT_STATUS_LABELS,
  MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
  MATCH_PURCHASE_ORDER_STATUSES,
  MATCH_PURCHASE_ORDER_STATUS_LABELS,
  MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
  INTERVIEW_STATUSES,
  INTERVIEW_STATUS_LABELS,
  INTERVIEW_STATUS_LABEL_KEYS,
  MATCH_WORKFLOW_STATUSES,
  MATCH_WORKFLOW_STATUS_LABELS,
  MATCH_WORKFLOW_STATUS_LABEL_KEYS,
  MATCH_WORKFLOW_STATUS_COLORS,
  ASSIGNMENT_STATUS_COLORS,
  PURCHASE_ORDER_STATUS_COLORS,
  INTERVIEW_STATUS_COLORS,
  getStatusLabel
} from "../../../constants/statuses";
import {
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  KpiStrip,
  KpiCard,
  FieldLabel
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

// Field value text inside detail Panels.
const FieldValue = ({ children, ...rest }) => (
  <Text
    fontSize="13px"
    fontWeight="600"
    color="portal.text"
    sx={{ marginBottom: "0 !important" }}
    {...rest}
  >
    {children}
  </Text>
);

const parseSkills = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return [];
    }
  }
  return [];
};

const isMatchPositioned = (m) => {
  return Boolean(
    m?.positioned ??
      m?.isPositioned ??
      m?.is_positioned ??
      m?.salesValidated ??
      m?.sales_validated
  );
};

// Skill chip group rendered inside detail Panels.
const SkillChips = ({ label, skills, colorScheme }) => (
  <Box mb="12px">
    <FieldLabel>{label}</FieldLabel>
    <HStack wrap="wrap" spacing="6px">
      {skills.map((skill, idx) => (
        <Badge
          key={`${label}-${idx}`}
          colorScheme={colorScheme}
          variant="subtle"
          fontSize="11px"
          px="8px"
          py="2px"
          borderRadius="999px"
        >
          {skill}
        </Badge>
      ))}
    </HStack>
  </Box>
);

const MatchDetailModal = ({ isOpen, onClose, matchId, initialMatch, initialNeed }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [matchData, setMatchData] = useState(initialMatch || null);
  const [need, setNeed] = useState(initialNeed || null);
  const [loadingMatch, setLoadingMatch] = useState(false);

  const [consultant, setConsultant] = useState(null);
  const [loadingConsultant, setLoadingConsultant] = useState(false);

  const [tracking, setTracking] = useState({
    assignmentStatus: "",
    assignmentStartDate: "",
    assignmentEndDate: "",
    purchaseOrderStatus: "",
    purchaseOrderNumber: "",
    purchaseOrderReceivedAt: "",
    purchaseOrderEndDate: "",
    atsScore: "",
    skillsScore: "",
    clientSatisfactionScore: "",
    clientSatisfactionComment: "",
    dailyRate: "",
    costRate: "",
    billableDaysTarget: ""
  });
  const [savingTracking, setSavingTracking] = useState(false);
  const trackingModal = useDisclosure();

  const [interviews, setInterviews] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [savingInterview, setSavingInterview] = useState(false);
  const interviewModal = useDisclosure();
  const [interviewForm, setInterviewForm] = useState({
    id: null,
    scheduledAt: "",
    completedAt: "",
    status: ""
  });

  // Workflow stepper state
  const [transitioning, setTransitioning] = useState(false);
  const backwardConfirm = useDisclosure();
  const [pendingBackwardTarget, setPendingBackwardTarget] = useState(null);
  const cancelBackwardRef = useRef();

  const workflowStatus = matchData?.workflowStatus || matchData?.workflow_status || null;
  const workflowStepIndex = useMemo(() => {
    if (!workflowStatus) return -1;
    return MATCH_WORKFLOW_STATUSES.findIndex((s) => s.value === workflowStatus);
  }, [workflowStatus]);
  const isExitStatus = workflowStatus === "WITHDRAWN" || workflowStatus === "REJECTED";

  const handleWorkflowTransition = async (targetStatus, force = false) => {
    if (!matchData?.id) return;
    setTransitioning(true);
    try {
      const updated = await transitionMatchWorkflow(
        matchData.id,
        targetStatus,
        force,
        { buId: selectedBusinessUnitId || undefined }
      );
      setMatchData(updated);
      toast({
        title: t("pilotMatches.detail.workflow.toasts.transitionSuccess", {
          defaultValue: "Workflow updated"
        }),
        status: "success"
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast({
        title: t("pilotMatches.detail.workflow.toasts.transitionFailedTitle", {
          defaultValue: "Transition failed"
        }),
        description:
          detail ||
          t("pilotMatches.detail.workflow.toasts.transitionFailedDescription", {
            defaultValue: "Could not update workflow status."
          }),
        status: "error"
      });
    } finally {
      setTransitioning(false);
    }
  };

  const handleStepClick = (stepIndex) => {
    if (transitioning) return;
    const target = MATCH_WORKFLOW_STATUSES[stepIndex]?.value;
    if (!target) return;
    // Same step = no-op
    if (stepIndex === workflowStepIndex) return;
    // If clicking backward, require confirmation
    if (workflowStepIndex >= 0 && stepIndex < workflowStepIndex) {
      setPendingBackwardTarget(target);
      backwardConfirm.onOpen();
      return;
    }
    handleWorkflowTransition(target);
  };

  const confirmBackwardTransition = () => {
    if (pendingBackwardTarget) {
      handleWorkflowTransition(pendingBackwardTarget, true);
    }
    setPendingBackwardTarget(null);
    backwardConfirm.onClose();
  };

  const toDateInput = (v) => {
    if (!v) return "";
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  const toDatetimeLocalInput = (v) => {
    if (!v) return "";
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    } catch {
      return "";
    }
  };

  const formatDatetimeLocalForDisplay = (v) => {
    if (!v) return "-";
    if (typeof v === "string" && v.includes("T")) return v.replace("T", " ");
    return String(v);
  };

  const fromDatetimeLocalToIso = (v) => {
    if (!v) return null;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    } catch {
      return null;
    }
  };

  const fetchMatch = useCallback(async () => {
    if (!matchId) return;
    setLoadingMatch(true);
    try {
      const data = await getMatch(matchId, { buId: selectedBusinessUnitId || undefined });
      setMatchData(data);
    } catch (e) {
      setMatchData(null);
    } finally {
      setLoadingMatch(false);
    }
  }, [matchId, selectedBusinessUnitId]);

  useEffect(() => {
    if (!isOpen) return;
    setMatchData(initialMatch || null);
    setNeed(initialNeed || null);
  }, [initialMatch, initialNeed, isOpen]);

  useEffect(() => {
    if (isOpen) return;
    trackingModal.onClose();
  }, [isOpen, trackingModal]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchMatch();
  }, [fetchMatch, isBUContextLoaded, isOpen]);

  useEffect(() => {
    if (!matchData) return;
    setTracking({
      assignmentStatus: matchData.assignment_status || matchData.assignmentStatus || "",
      assignmentStartDate: toDateInput(
        matchData.assignment_start_date || matchData.assignmentStartDate
      ),
      assignmentEndDate: toDateInput(
        matchData.assignment_end_date || matchData.assignmentEndDate
      ),
      purchaseOrderStatus: matchData.purchase_order_status || matchData.purchaseOrderStatus || "",
      purchaseOrderNumber: matchData.purchase_order_number || matchData.purchaseOrderNumber || "",
      purchaseOrderReceivedAt: toDatetimeLocalInput(
        matchData.purchase_order_received_at || matchData.purchaseOrderReceivedAt
      ),
      purchaseOrderEndDate: toDateInput(
        matchData.purchase_order_end_date || matchData.purchaseOrderEndDate
      ),
      atsScore: matchData.atsScore ?? matchData.ats_score ?? "",
      skillsScore: matchData.skillsScore ?? matchData.skills_score ?? "",
      clientSatisfactionScore:
        matchData.client_satisfaction_score ?? matchData.clientSatisfactionScore ?? "",
      clientSatisfactionComment:
        matchData.client_satisfaction_comment || matchData.clientSatisfactionComment || "",
      dailyRate: matchData.dailyRate ?? matchData.daily_rate ?? "",
      costRate: matchData.costRate ?? matchData.cost_rate ?? "",
      billableDaysTarget:
        matchData.billableDaysTarget ?? matchData.billable_days_target ?? ""
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchData]);

  const normalizeInterview = (raw) => {
    if (!raw) return null;
    return {
      id: raw.id,
      matchId: raw.match_id ?? raw.matchId,
      scheduledAt: raw.scheduled_at ?? raw.scheduledAt,
      completedAt: raw.completed_at ?? raw.completedAt,
      status: raw.status || ""
    };
  };

  const interviewStatusColor = (status) => INTERVIEW_STATUS_COLORS[status] || "gray";

  const formatInterviewDatetime = (value) => {
    if (!value) return "-";
    const localValue = toDatetimeLocalInput(value);
    return localValue ? formatDatetimeLocalForDisplay(localValue) : "-";
  };

  const resetInterviewForm = () => {
    setInterviewForm({
      id: null,
      scheduledAt: "",
      completedAt: "",
      status: ""
    });
  };

  const openCreateInterview = () => {
    resetInterviewForm();
    interviewModal.onOpen();
  };

  const openEditInterview = (interview) => {
    if (!interview) return;
    setInterviewForm({
      id: interview.id,
      scheduledAt: toDatetimeLocalInput(interview.scheduledAt),
      completedAt: toDatetimeLocalInput(interview.completedAt),
      status: interview.status || ""
    });
    interviewModal.onOpen();
  };

  const fetchInterviews = useCallback(async () => {
    if (!matchId) return;
    setLoadingInterviews(true);
    try {
      const data = await listInterviewsForMatch(matchId, {
        buId: selectedBusinessUnitId || undefined
      });
      const normalized = Array.isArray(data)
        ? data.map(normalizeInterview).filter(Boolean)
        : [];
      setInterviews(normalized);
    } catch (e) {
      setInterviews([]);
      toast({
        title: t("pilotMatches.detail.interviews.toasts.loadFailedTitle"),
        status: "error"
      });
    } finally {
      setLoadingInterviews(false);
    }
  }, [matchId, selectedBusinessUnitId, t, toast]);

  useEffect(() => {
    if (!isOpen) return;
    if (!matchId) return;
    fetchInterviews();
  }, [fetchInterviews, isOpen, matchId]);

  useEffect(() => {
    const consultantId = matchData?.consultant_id || matchData?.consultantId;
    if (!consultantId || !isOpen) return;

    const fetchConsultant = async () => {
      setLoadingConsultant(true);
      try {
        const data = await getConsultant(consultantId, {
          buId: selectedBusinessUnitId || undefined
        });
        setConsultant(data);
      } catch (e) {
        toast({
          title: t("pilotConsultants.detail.toasts.loadConsultantFailed"),
          status: "error"
        });
        setConsultant(null);
      } finally {
        setLoadingConsultant(false);
      }
    };

    fetchConsultant();
  }, [isOpen, matchData, selectedBusinessUnitId, toast, t]);

  const handleSaveTracking = async () => {
    if (!matchData?.id) return;
    setSavingTracking(true);
    try {
      const payload = {
        assignmentStatus: tracking.assignmentStatus || null,
        assignmentStartDate: tracking.assignmentStartDate || null,
        assignmentEndDate: tracking.assignmentEndDate || null,
        purchaseOrderStatus: tracking.purchaseOrderStatus || null,
        purchaseOrderNumber: tracking.purchaseOrderNumber || null,
        purchaseOrderReceivedAt: fromDatetimeLocalToIso(tracking.purchaseOrderReceivedAt),
        purchaseOrderEndDate: tracking.purchaseOrderEndDate || null,
        atsScore: tracking.atsScore === "" ? null : Number(tracking.atsScore),
        skillsScore: tracking.skillsScore === "" ? null : Number(tracking.skillsScore),
        clientSatisfactionScore:
          tracking.clientSatisfactionScore === ""
            ? null
            : Number(tracking.clientSatisfactionScore),
        clientSatisfactionComment: tracking.clientSatisfactionComment || null,
        dailyRate: tracking.dailyRate === "" ? null : Number(tracking.dailyRate),
        costRate: tracking.costRate === "" ? null : Number(tracking.costRate),
        billableDaysTarget:
          tracking.billableDaysTarget === "" ? null : Number(tracking.billableDaysTarget)
      };
      const updated = await updateMatch(matchData.id, payload, {
        buId: selectedBusinessUnitId || undefined
      });
      setMatchData(updated || matchData);
      await fetchMatch();
      toast({
        title: t("pilotMatches.detail.missionTracking.toasts.saveSuccess"),
        status: "success"
      });
    } catch (e) {
      toast({
        title: t("pilotMatches.detail.missionTracking.toasts.saveFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotMatches.detail.missionTracking.toasts.saveFailedDescription"),
        status: "error"
      });
    } finally {
      setSavingTracking(false);
    }
  };

  const handleSaveInterview = async () => {
    const targetMatchId = matchData?.id || matchId;
    if (!targetMatchId) return false;

    const hasScheduled = Boolean(interviewForm.scheduledAt);
    const hasCompleted = Boolean(interviewForm.completedAt);
    const hasStatus = Boolean(interviewForm.status);

    if (!interviewForm.id && !hasScheduled && !hasCompleted) {
      toast({
        title: t("pilotMatches.detail.interviews.toasts.missingTime"),
        status: "warning"
      });
      return false;
    }

    if (interviewForm.id && !hasScheduled && !hasCompleted && !hasStatus) {
      toast({
        title: t("pilotMatches.detail.interviews.toasts.nothingToUpdate"),
        status: "warning"
      });
      return false;
    }

    setSavingInterview(true);
    try {
      const payload = {};
      if (hasScheduled) {
        payload.scheduledAt = fromDatetimeLocalToIso(interviewForm.scheduledAt);
      }
      if (hasCompleted) {
        payload.completedAt = fromDatetimeLocalToIso(interviewForm.completedAt);
      }
      if (hasStatus) {
        payload.status = interviewForm.status;
      }

      if (interviewForm.id) {
        await updateInterview(interviewForm.id, payload, {
          buId: selectedBusinessUnitId || undefined
        });
      } else {
        await createInterviewForMatch(targetMatchId, payload, {
          buId: selectedBusinessUnitId || undefined
        });
      }

      await fetchInterviews();
      toast({
        title: t("pilotMatches.detail.interviews.toasts.saveSuccess"),
        status: "success"
      });
      return true;
    } catch (e) {
      toast({
        title: t("pilotMatches.detail.interviews.toasts.saveFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotMatches.detail.interviews.toasts.saveFailedDescription"),
        status: "error"
      });
      return false;
    } finally {
      setSavingInterview(false);
    }
  };

  const needSkillsTechnical = useMemo(
    () => parseSkills(need?.skillsTechnical || need?.skills_technical),
    [need]
  );
  const needSkillsFunctional = useMemo(
    () => parseSkills(need?.skillsFunctional || need?.skills_functional),
    [need]
  );
  const needSkillsBusiness = useMemo(
    () => parseSkills(need?.skillsBusiness || need?.skills_business),
    [need]
  );

  const consultantSkillsTechnical = useMemo(
    () => parseSkills(consultant?.skillsTechnical || consultant?.skills_technical),
    [consultant]
  );
  const consultantSkillsFunctional = useMemo(
    () => parseSkills(consultant?.skillsFunctional || consultant?.skills_functional),
    [consultant]
  );
  const consultantSkillsBusiness = useMemo(
    () => parseSkills(consultant?.skillsBusiness || consultant?.skills_business),
    [consultant]
  );

  const handleClose = () => {
    trackingModal.onClose();
    interviewModal.onClose();
    resetInterviewForm();
    onClose?.();
  };

  // Header title: consultant <-> need when context is present, else "Match #id".
  const consultantDisplayName =
    matchData?.consultantName ||
    matchData?.consultant_name ||
    consultant?.name ||
    consultant?.fullName ||
    null;
  const needTitle = need?.title || matchData?.needTitle || matchData?.need_title || null;
  const headerTitle =
    consultantDisplayName && needTitle
      ? `${consultantDisplayName} ↔ ${needTitle}`
      : t("pilotMatches.detail.pageTitle", { id: matchData?.id || matchId || "-" });

  // ---- Interviews tab DataTable columns ----
  const interviewColumns = [
    { key: "id", label: t("pilotMatches.detail.fields.id"), width: "70px" },
    { key: "status", label: t("pilotMatches.detail.interviews.fields.status"), width: "22%" },
    { key: "scheduledAt", label: t("pilotMatches.detail.interviews.fields.scheduledAt") },
    { key: "completedAt", label: t("pilotMatches.detail.interviews.fields.completedAt") },
    {
      key: "actions",
      label: t("pilotMatches.detail.table.actions"),
      width: "70px",
      align: "right"
    }
  ];

  const interviewsEmpty = (
    <EmptyState
      icon={<FaCalendarAlt />}
      title={t("pilotMatches.detail.interviews.emptyState")}
      hint={t("pilotMatches.detail.interviews.description")}
    />
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="6xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX} pr="56px">
            <Flex align="center" justify="space-between" gap="12px" flexWrap="wrap">
              <HStack spacing="12px" minW={0}>
                <Heading
                  as="h2"
                  fontSize="17px"
                  fontWeight={800}
                  letterSpacing="-0.02em"
                  color="portal.text"
                  isTruncated
                  sx={{ marginBottom: "0 !important" }}
                >
                  {headerTitle}
                </Heading>
                {workflowStatus && (
                  <StatusBadge
                    label={getStatusLabel(
                      t,
                      MATCH_WORKFLOW_STATUS_LABEL_KEYS,
                      MATCH_WORKFLOW_STATUS_LABELS,
                      workflowStatus
                    )}
                    colorScheme={MATCH_WORKFLOW_STATUS_COLORS[workflowStatus] || "gray"}
                  />
                )}
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody bg="portal.bg" py="14px">
            <Tabs
              variant="enclosed"
              colorScheme="blue"
              isFitted
              sx={{
                "& [role=tab]": {
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "portal.muted"
                },
                "& [role=tab][aria-selected=true]": {
                  color: "portal.primary",
                  borderColor: "portal.line",
                  borderBottomColor: "transparent"
                }
              }}
            >
              <TabList>
                <Tab>{t("pilotMatches.detail.sections.overview")}</Tab>
                <Tab>{t("pilotMatches.detail.sections.needInfo")}</Tab>
                <Tab>{t("pilotMatches.detail.sections.consultantInfo")}</Tab>
                <Tab>{t("pilotMatches.detail.interviews.title")}</Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Overview */}
                <TabPanel px={0} pt="14px">
                  {loadingMatch ? (
                    <Panel title={t("pilotMatches.detail.sections.overview")}>
                      <Flex align="center" justify="center" py="40px">
                        <Spinner color="portal.primary" />
                      </Flex>
                    </Panel>
                  ) : !matchData ? (
                    <Panel>
                      <EmptyState title={t("pilotMatches.detail.notFound")} />
                    </Panel>
                  ) : (
                    <VStackOverview
                      t={t}
                      matchData={matchData}
                      workflowStatus={workflowStatus}
                      workflowStepIndex={workflowStepIndex}
                      isExitStatus={isExitStatus}
                      transitioning={transitioning}
                      tracking={tracking}
                      onStepClick={handleStepClick}
                      onWorkflowTransition={handleWorkflowTransition}
                      onOpenTracking={trackingModal.onOpen}
                      formatDatetimeLocalForDisplay={formatDatetimeLocalForDisplay}
                    />
                  )}
                </TabPanel>

                {/* Tab 2: Need */}
                <TabPanel px={0} pt="14px">
                  <Panel title={t("pilotMatches.detail.sections.needInfo")}>
                    {!need ? (
                      <EmptyState title={t("pilotMatches.detail.noNeedInContext")} />
                    ) : (
                      <Box p="12px">
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
                          <Box>
                            <FieldLabel>{t("pilotNeeds.detail.sections.client")}</FieldLabel>
                            <FieldValue>{need.clientName || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotNeeds.detail.sections.date")}</FieldLabel>
                            <FieldValue>{need.date || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotNeeds.detail.sections.status")}</FieldLabel>
                            {need.status ? (
                              <StatusBadge
                                label={getStatusLabel(
                                  t,
                                  NEED_STATUS_LABEL_KEYS,
                                  NEED_STATUS_LABELS,
                                  need.status
                                )}
                                colorScheme={NEED_STATUS_COLORS[need.status] || "gray"}
                              />
                            ) : (
                              <FieldValue>-</FieldValue>
                            )}
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotNeeds.detail.sections.title")}</FieldLabel>
                            <FieldValue>{need.title || "-"}</FieldValue>
                          </Box>
                        </SimpleGrid>

                        <Divider my="14px" borderColor="portal.line" />

                        <Box mb="12px">
                          <FieldLabel>{t("pilotNeeds.detail.sections.description")}</FieldLabel>
                          <Text
                            fontSize="13px"
                            color="portal.text"
                            whiteSpace="pre-wrap"
                            sx={{ marginBottom: "0 !important" }}
                          >
                            {need.description || t("pilotNeeds.detail.sections.noDescription")}
                          </Text>
                        </Box>

                        {(needSkillsTechnical.length > 0 ||
                          needSkillsFunctional.length > 0 ||
                          needSkillsBusiness.length > 0) && (
                          <>
                            <Divider my="14px" borderColor="portal.line" />
                            {needSkillsTechnical.length > 0 && (
                              <SkillChips
                                label={t("pilotNeeds.detail.sections.technicalSkills")}
                                skills={needSkillsTechnical}
                                colorScheme="blue"
                              />
                            )}
                            {needSkillsFunctional.length > 0 && (
                              <SkillChips
                                label={t("pilotNeeds.detail.sections.functionalSkills")}
                                skills={needSkillsFunctional}
                                colorScheme="orange"
                              />
                            )}
                            {needSkillsBusiness.length > 0 && (
                              <SkillChips
                                label={t("pilotNeeds.detail.sections.businessSkills")}
                                skills={needSkillsBusiness}
                                colorScheme="green"
                              />
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Panel>
                </TabPanel>

                {/* Tab 3: Consultant */}
                <TabPanel px={0} pt="14px">
                  <Panel title={t("pilotMatches.detail.sections.consultantInfo")}>
                    {loadingConsultant ? (
                      <Flex align="center" justify="center" py="40px">
                        <Spinner color="portal.primary" />
                      </Flex>
                    ) : !consultant ? (
                      <EmptyState title={t("pilotMatches.detail.noConsultantInContext")} />
                    ) : (
                      <Box p="12px">
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
                          <Box>
                            <FieldLabel>{t("pilotConsultants.detail.sections.title")}</FieldLabel>
                            <FieldValue>{consultant.title || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotConsultants.detail.sections.email")}</FieldLabel>
                            <FieldValue>{consultant.email || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotConsultants.detail.sections.phone")}</FieldLabel>
                            <FieldValue>{consultant.phone || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotConsultants.detail.sections.location")}</FieldLabel>
                            <FieldValue>{consultant.location || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>
                              {t("pilotConsultants.detail.sections.availability")}
                            </FieldLabel>
                            <FieldValue>{consultant.availability || "-"}</FieldValue>
                          </Box>
                          <Box>
                            <FieldLabel>{t("pilotConsultants.detail.sections.status")}</FieldLabel>
                            <FieldValue>
                              {getStatusLabel(
                                t,
                                CONSULTANT_STATUS_LABEL_KEYS,
                                CONSULTANT_STATUS_LABELS,
                                consultant.status
                              ) || "-"}
                            </FieldValue>
                          </Box>
                        </SimpleGrid>

                        {consultant.description && (
                          <>
                            <Divider my="14px" borderColor="portal.line" />
                            <Box mb="12px">
                              <FieldLabel>
                                {t("pilotConsultants.detail.sections.description")}
                              </FieldLabel>
                              <Text
                                fontSize="13px"
                                color="portal.text"
                                whiteSpace="pre-wrap"
                                sx={{ marginBottom: "0 !important" }}
                              >
                                {consultant.description}
                              </Text>
                            </Box>
                          </>
                        )}

                        {(consultantSkillsTechnical.length > 0 ||
                          consultantSkillsFunctional.length > 0 ||
                          consultantSkillsBusiness.length > 0) && (
                          <>
                            <Divider my="14px" borderColor="portal.line" />
                            {consultantSkillsTechnical.length > 0 && (
                              <SkillChips
                                label={t("pilotConsultants.detail.sections.technicalSkills")}
                                skills={consultantSkillsTechnical}
                                colorScheme="blue"
                              />
                            )}
                            {consultantSkillsFunctional.length > 0 && (
                              <SkillChips
                                label={t("pilotConsultants.detail.sections.functionalSkills")}
                                skills={consultantSkillsFunctional}
                                colorScheme="orange"
                              />
                            )}
                            {consultantSkillsBusiness.length > 0 && (
                              <SkillChips
                                label={t("pilotConsultants.detail.sections.businessSkills")}
                                skills={consultantSkillsBusiness}
                                colorScheme="green"
                              />
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Panel>
                </TabPanel>

                {/* Tab 4: Interviews */}
                <TabPanel px={0} pt="14px">
                  <Panel
                    title={t("pilotMatches.detail.interviews.title")}
                    count={loadingInterviews ? undefined : interviews.length}
                    actions={
                      <Button
                        size="sm"
                        leftIcon={<AddIcon />}
                        variant="primary"
                        onClick={openCreateInterview}
                      >
                        {t("pilotMatches.detail.interviews.actions.add")}
                      </Button>
                    }
                  >
                    <DataTable
                      columns={interviewColumns}
                      rows={interviews}
                      loading={loadingInterviews}
                      emptyState={interviewsEmpty}
                      renderRow={(interview) => (
                        <Tr key={interview.id}>
                          <Td fontWeight="700">#{interview.id}</Td>
                          <Td>
                            {interview.status ? (
                              <StatusBadge
                                label={getStatusLabel(
                                  t,
                                  INTERVIEW_STATUS_LABEL_KEYS,
                                  INTERVIEW_STATUS_LABELS,
                                  interview.status
                                )}
                                colorScheme={interviewStatusColor(interview.status)}
                              />
                            ) : (
                              "-"
                            )}
                          </Td>
                          <Td>{formatInterviewDatetime(interview.scheduledAt)}</Td>
                          <Td>{formatInterviewDatetime(interview.completedAt)}</Td>
                          <Td textAlign="right">
                            <MiniIconButton
                              aria-label={t("pilotMatches.detail.interviews.actions.edit")}
                              icon={<EditIcon />}
                              onClick={() => openEditInterview(interview)}
                            />
                          </Td>
                        </Tr>
                      )}
                    />
                  </Panel>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              {t("pilotMatches.detail.missionTracking.actions.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Interview create/edit modal */}
      <Modal
        isOpen={interviewModal.isOpen}
        onClose={() => {
          interviewModal.onClose();
          resetInterviewForm();
        }}
        size="lg"
      >
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotMatches.detail.interviews.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="13px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
              {t("pilotMatches.detail.interviews.description")}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
              <Box>
                <FieldLabel>{t("pilotMatches.detail.interviews.fields.status")}</FieldLabel>
                <Select
                  size="sm"
                  value={interviewForm.status}
                  onChange={(e) =>
                    setInterviewForm((s) => ({ ...s, status: e.target.value }))
                  }
                >
                  <option value="">{t("pilotMatches.detail.interviews.fields.empty")}</option>
                  {INTERVIEW_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        INTERVIEW_STATUS_LABEL_KEYS,
                        INTERVIEW_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <FieldLabel>{t("pilotMatches.detail.interviews.fields.scheduledAt")}</FieldLabel>
                <Input
                  size="sm"
                  type="datetime-local"
                  value={interviewForm.scheduledAt}
                  onChange={(e) =>
                    setInterviewForm((s) => ({ ...s, scheduledAt: e.target.value }))
                  }
                />
              </Box>
              <Box>
                <FieldLabel>{t("pilotMatches.detail.interviews.fields.completedAt")}</FieldLabel>
                <Input
                  size="sm"
                  type="datetime-local"
                  value={interviewForm.completedAt}
                  onChange={(e) =>
                    setInterviewForm((s) => ({ ...s, completedAt: e.target.value }))
                  }
                />
              </Box>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                interviewModal.onClose();
                resetInterviewForm();
              }}
            >
              {t("pilotMatches.detail.interviews.actions.close")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                const saved = await handleSaveInterview();
                if (saved) {
                  interviewModal.onClose();
                  resetInterviewForm();
                }
              }}
              isLoading={savingInterview}
            >
              {t("pilotMatches.detail.interviews.actions.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assignment tracking modal */}
      <Modal isOpen={trackingModal.isOpen} onClose={trackingModal.onClose} size="2xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotMatches.detail.missionTracking.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="13px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
              {t("pilotMatches.detail.missionTracking.description")}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.assignmentStatus")}
                </FieldLabel>
                <Select
                  size="sm"
                  value={tracking.assignmentStatus}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, assignmentStatus: e.target.value }))
                  }
                >
                  <option value="">{t("pilotMatches.detail.missionTracking.fields.empty")}</option>
                  {MATCH_ASSIGNMENT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
                        MATCH_ASSIGNMENT_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderStatus")}
                </FieldLabel>
                <Select
                  size="sm"
                  value={tracking.purchaseOrderStatus}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderStatus: e.target.value }))
                  }
                >
                  <option value="">{t("pilotMatches.detail.missionTracking.fields.empty")}</option>
                  {MATCH_PURCHASE_ORDER_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
                        MATCH_PURCHASE_ORDER_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.assignmentStartDate")}
                </FieldLabel>
                <Input
                  size="sm"
                  type="date"
                  value={tracking.assignmentStartDate}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, assignmentStartDate: e.target.value }))
                  }
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.assignmentEndDate")}
                </FieldLabel>
                <Input
                  size="sm"
                  type="date"
                  value={tracking.assignmentEndDate}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, assignmentEndDate: e.target.value }))
                  }
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderNumber")}
                </FieldLabel>
                <Input
                  size="sm"
                  value={tracking.purchaseOrderNumber}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderNumber: e.target.value }))
                  }
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderReceivedAt")}
                </FieldLabel>
                <Input
                  size="sm"
                  type="datetime-local"
                  value={tracking.purchaseOrderReceivedAt}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderReceivedAt: e.target.value }))
                  }
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderEndDate", {
                    defaultValue: "Purchase order end date"
                  })}
                </FieldLabel>
                <Input
                  size="sm"
                  type="date"
                  value={tracking.purchaseOrderEndDate}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderEndDate: e.target.value }))
                  }
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.fields.atsScore", { defaultValue: "ATS score" })}
                </FieldLabel>
                <Input
                  size="sm"
                  type="number"
                  step="0.01"
                  value={tracking.atsScore}
                  onChange={(e) => setTracking((s) => ({ ...s, atsScore: e.target.value }))}
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.fields.skillsScore", { defaultValue: "Skills score" })}
                </FieldLabel>
                <Input
                  size="sm"
                  type="number"
                  step="0.01"
                  value={tracking.skillsScore}
                  onChange={(e) => setTracking((s) => ({ ...s, skillsScore: e.target.value }))}
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.clientSatisfactionScore")}
                </FieldLabel>
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  value={tracking.clientSatisfactionScore}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setTracking((s) => ({ ...s, clientSatisfactionScore: value }));
                      return;
                    }
                    const numericValue = Number(value);
                    if (!Number.isInteger(numericValue)) return;
                    if (numericValue < 1 || numericValue > 10) return;
                    setTracking((s) => ({ ...s, clientSatisfactionScore: value }));
                  }}
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.clientSatisfactionComment")}
                </FieldLabel>
                <Textarea
                  size="sm"
                  rows={3}
                  value={tracking.clientSatisfactionComment}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, clientSatisfactionComment: e.target.value }))
                  }
                />
              </Box>
              <Box data-testid="financials-section">
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.dailyRate", {
                    defaultValue: "Daily rate (€)"
                  })}
                </FieldLabel>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  step="0.01"
                  value={tracking.dailyRate}
                  data-testid="input-daily-rate"
                  onChange={(e) => setTracking((s) => ({ ...s, dailyRate: e.target.value }))}
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.costRate", {
                    defaultValue: "Cost rate (€)"
                  })}
                </FieldLabel>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  step="0.01"
                  value={tracking.costRate}
                  data-testid="input-cost-rate"
                  onChange={(e) => setTracking((s) => ({ ...s, costRate: e.target.value }))}
                />
              </Box>
              <Box>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.billableDaysTarget", {
                    defaultValue: "Billable days target"
                  })}
                </FieldLabel>
                <Input
                  size="sm"
                  type="number"
                  min={0}
                  step={1}
                  value={tracking.billableDaysTarget}
                  data-testid="input-billable-days-target"
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, billableDaysTarget: e.target.value }))
                  }
                />
              </Box>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={trackingModal.onClose}>
              {t("pilotMatches.detail.missionTracking.actions.close")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                await handleSaveTracking();
                trackingModal.onClose();
              }}
              isLoading={savingTracking}
            >
              {t("pilotMatches.detail.missionTracking.actions.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Backward transition confirmation dialog */}
      <AlertDialog
        isOpen={backwardConfirm.isOpen}
        leastDestructiveRef={cancelBackwardRef}
        onClose={() => {
          setPendingBackwardTarget(null);
          backwardConfirm.onClose();
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent sx={MODAL_CONTENT_SX}>
            <AlertDialogHeader sx={MODAL_HEADER_SX}>
              {t("pilotMatches.detail.workflow.backwardConfirm.title", {
                defaultValue: "Revert workflow step?"
              })}
            </AlertDialogHeader>
            <AlertDialogBody py="16px" fontSize="13px" color="portal.text">
              {t("pilotMatches.detail.workflow.backwardConfirm.body", {
                defaultValue:
                  "Moving backward in the workflow pipeline will reset the match to an earlier stage. Are you sure?"
              })}
            </AlertDialogBody>
            <AlertDialogFooter sx={MODAL_FOOTER_SX}>
              <Button
                variant="secondary"
                size="sm"
                ref={cancelBackwardRef}
                onClick={() => {
                  setPendingBackwardTarget(null);
                  backwardConfirm.onClose();
                }}
              >
                {t("common.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={confirmBackwardTransition}
                isLoading={transitioning}
              >
                {t("common.confirm", { defaultValue: "Confirm" })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

// ---------------------------------------------------------------------------
// Overview tab body — extracted to keep the main render readable. Renders the
// workflow stepper, KPI strip, reasoning panel and the assignment-summary
// panel. Pure presentational; all handlers/state passed in via props.
// ---------------------------------------------------------------------------
const VStackOverview = ({
  t,
  matchData,
  workflowStatus,
  workflowStepIndex,
  isExitStatus,
  transitioning,
  tracking,
  onStepClick,
  onWorkflowTransition,
  onOpenTracking,
  formatDatetimeLocalForDisplay
}) => {
  const trackingFields = [
    {
      label: t("pilotMatches.detail.missionTracking.fields.assignmentStatus"),
      value: tracking.assignmentStatus ? (
        <StatusBadge
          label={getStatusLabel(
            t,
            MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
            MATCH_ASSIGNMENT_STATUS_LABELS,
            tracking.assignmentStatus
          )}
          colorScheme={ASSIGNMENT_STATUS_COLORS[tracking.assignmentStatus] || "gray"}
        />
      ) : (
        "-"
      )
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.purchaseOrderStatus"),
      value: tracking.purchaseOrderStatus ? (
        <StatusBadge
          label={getStatusLabel(
            t,
            MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
            MATCH_PURCHASE_ORDER_STATUS_LABELS,
            tracking.purchaseOrderStatus
          )}
          colorScheme={PURCHASE_ORDER_STATUS_COLORS[tracking.purchaseOrderStatus] || "gray"}
        />
      ) : (
        "-"
      )
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.assignmentStartDate"),
      value: tracking.assignmentStartDate || "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.assignmentEndDate"),
      value: tracking.assignmentEndDate || "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.purchaseOrderNumber"),
      value: tracking.purchaseOrderNumber || "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.purchaseOrderReceivedAt"),
      value: formatDatetimeLocalForDisplay(tracking.purchaseOrderReceivedAt)
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.purchaseOrderEndDate", {
        defaultValue: "Purchase order end date"
      }),
      value: tracking.purchaseOrderEndDate || "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.clientSatisfactionScore"),
      value:
        tracking.clientSatisfactionScore !== "" && tracking.clientSatisfactionScore != null
          ? tracking.clientSatisfactionScore
          : "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.clientSatisfactionComment"),
      value: tracking.clientSatisfactionComment || "-",
      preWrap: true
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.dailyRate", {
        defaultValue: "Daily rate"
      }),
      value:
        tracking.dailyRate !== "" && tracking.dailyRate != null
          ? `${tracking.dailyRate} €`
          : "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.costRate", {
        defaultValue: "Cost rate"
      }),
      value:
        tracking.costRate !== "" && tracking.costRate != null
          ? `${tracking.costRate} €`
          : "-"
    },
    {
      label: t("pilotMatches.detail.missionTracking.fields.billableDaysTarget", {
        defaultValue: "Billable days target"
      }),
      value:
        tracking.billableDaysTarget !== "" && tracking.billableDaysTarget != null
          ? tracking.billableDaysTarget
          : "-"
    }
  ];

  return (
    <Box display="flex" flexDirection="column" gap="10px">
      {/* Workflow Pipeline Panel */}
      <Panel title={t("pilotMatches.detail.workflow.title", { defaultValue: "Workflow Pipeline" })}>
        <Box p="12px">
          {isExitStatus ? (
            <Flex align="center" gap="8px">
              <Icon
                as={WarningIcon}
                color={workflowStatus === "REJECTED" ? "danger.500" : "portal.muted"}
              />
              <StatusBadge
                label={getStatusLabel(
                  t,
                  MATCH_WORKFLOW_STATUS_LABEL_KEYS,
                  MATCH_WORKFLOW_STATUS_LABELS,
                  workflowStatus
                )}
                colorScheme={MATCH_WORKFLOW_STATUS_COLORS[workflowStatus] || "gray"}
              />
            </Flex>
          ) : (
            <Stepper
              index={workflowStepIndex >= 0 ? workflowStepIndex : -1}
              colorScheme="blue"
              size="sm"
              sx={{
                "& .chakra-step__title": {
                  lineHeight: "normal !important",
                  marginBottom: "0 !important",
                  fontSize: "12px",
                  fontWeight: 700
                }
              }}
            >
              {MATCH_WORKFLOW_STATUSES.map((step, index) => (
                <Step key={step.value}>
                  <Tooltip
                    label={
                      index <= workflowStepIndex
                        ? t("pilotMatches.detail.workflow.clickToRevert", {
                            defaultValue: "Click to revert (requires confirmation)"
                          })
                        : t("pilotMatches.detail.workflow.clickToAdvance", {
                            defaultValue: "Click to advance"
                          })
                    }
                  >
                    <StepIndicator
                      cursor="pointer"
                      onClick={() => onStepClick(index)}
                      _hover={{ transform: "scale(1.15)", transition: "transform 0.15s" }}
                    >
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>
                  </Tooltip>
                  <Box flexShrink={0}>
                    <StepTitle>
                      {getStatusLabel(
                        t,
                        MATCH_WORKFLOW_STATUS_LABEL_KEYS,
                        MATCH_WORKFLOW_STATUS_LABELS,
                        step.value
                      )}
                    </StepTitle>
                  </Box>
                  <StepSeparator />
                </Step>
              ))}
            </Stepper>
          )}

          {/* Withdraw / Mark Rejected buttons */}
          {!isExitStatus && workflowStepIndex >= 1 && (
            <HStack spacing="8px" mt="14px">
              <Button
                size="sm"
                variant="secondary"
                isLoading={transitioning}
                onClick={() => onWorkflowTransition("WITHDRAWN")}
              >
                {t("pilotMatches.detail.workflow.withdraw", { defaultValue: "Withdraw" })}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                color="portal.danger"
                borderColor="portal.danger"
                _hover={{ bg: "danger.50", borderColor: "portal.danger" }}
                isLoading={transitioning}
                onClick={() => onWorkflowTransition("REJECTED")}
              >
                {t("pilotMatches.detail.workflow.markRejected", { defaultValue: "Mark Rejected" })}
              </Button>
            </HStack>
          )}
        </Box>
      </Panel>

      {/* Scores KPI strip */}
      <KpiStrip columns={{ base: 1, sm: 2, lg: 4 }}>
        <KpiCard
          label={t("pilotMatches.detail.fields.score")}
          value={matchData.score != null ? matchData.score : "-"}
        />
        <KpiCard
          label={t("pilotMatches.detail.fields.atsScore", { defaultValue: "ATS score" })}
          value={matchData.atsScore ?? matchData.ats_score ?? "-"}
        />
        <KpiCard
          label={t("pilotMatches.detail.fields.skillsScore", { defaultValue: "Skills score" })}
          value={matchData.skillsScore ?? matchData.skills_score ?? "-"}
        />
        <KpiCard
          label={t("pilotMatches.detail.table.positioned")}
          value={
            isMatchPositioned(matchData)
              ? t("pilotMatches.detail.positioned.yes")
              : t("pilotMatches.detail.positioned.no")
          }
        />
      </KpiStrip>

      {/* Source + Reasoning */}
      <Panel title={t("pilotMatches.detail.sections.reasoning")}>
        <Box p="12px">
          <Box mb="12px">
            <FieldLabel>{t("pilotMatches.detail.fields.source")}</FieldLabel>
            <FieldValue>{matchData.source || "-"}</FieldValue>
          </Box>
          <Text
            fontSize="13px"
            color="portal.text"
            whiteSpace="pre-wrap"
            sx={{ marginBottom: "0 !important" }}
          >
            {matchData.reasoning || t("pilotMatches.detail.noReasoning")}
          </Text>
        </Box>
      </Panel>

      {/* Assignment summary */}
      <Panel
        title={t("pilotMatches.detail.missionTracking.title")}
        actions={
          <Button size="sm" variant="secondary" leftIcon={<EditIcon />} onClick={onOpenTracking}>
            {t("pilotMatches.detail.missionTracking.actions.open")}
          </Button>
        }
      >
        <Box p="12px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
            {trackingFields.map((field, idx) => (
              <Box key={idx}>
                <FieldLabel>{field.label}</FieldLabel>
                {typeof field.value === "string" || typeof field.value === "number" ? (
                  <FieldValue whiteSpace={field.preWrap ? "pre-wrap" : undefined}>
                    {field.value}
                  </FieldValue>
                ) : (
                  field.value
                )}
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      </Panel>
    </Box>
  );
};

export default MatchDetailModal;
